import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to get the user's active Unipile calendar account
 * This is required for all calendar edge function calls
 *
 * @returns The active calendar account_id or null if none found
 */
export const useActiveCalendarAccount = () => {
  return useQuery({
    queryKey: ['active-calendar-account'],
    queryFn: async () => {
      try {
        // Call unipile-manager to get active accounts
        const { data, error } = await supabase.functions.invoke(
          'unipile-manager?command=get-active-accounts-for-user',
          {
            method: 'GET'
          }
        );

        if (error) {
          console.error('[useActiveCalendarAccount] Error fetching accounts:', error);
          throw error;
        }

        if (!data || !Array.isArray(data)) {
          console.warn('[useActiveCalendarAccount] No accounts data returned');
          return null;
        }

        // Find first active calendar account (GOOGLE, OUTLOOK, GOOGLE_OAUTH)
        const calendarAccount = data.find(
          (acc: any) => acc.active && ['GOOGLE', 'OUTLOOK', 'GOOGLE_OAUTH'].includes(acc.type)
        );

        if (!calendarAccount) {
          console.warn('[useActiveCalendarAccount] No active calendar account found');
          return null;
        }

        console.log('[useActiveCalendarAccount] Active account found:', calendarAccount.account_id, calendarAccount.type);
        return calendarAccount.account_id;
      } catch (err) {
        console.error('[useActiveCalendarAccount] Exception:', err);
        return null;
      }
    },
    // Cache for 5 minutes to avoid excessive calls
    staleTime: 5 * 60 * 1000,
    // Retry once on failure
    retry: 1,
  });
};

/**
 * Hook to get all active calendar accounts (for multi-account support in the future)
 */
const useActiveCalendarAccounts = () => {
  return useQuery({
    queryKey: ['active-calendar-accounts'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          'unipile-manager?command=get-active-accounts-for-user',
          {
            method: 'GET'
          }
        );

        if (error) throw error;

        if (!data || !Array.isArray(data)) {
          return [];
        }

        // Return all active calendar accounts
        return data.filter(
          (acc: any) => acc.active && ['GOOGLE', 'OUTLOOK', 'GOOGLE_OAUTH'].includes(acc.type)
        );
      } catch (err) {
        console.error('[useActiveCalendarAccounts] Error:', err);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};
